import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import {
    Search, Filter, ShieldCheck, Users, Plane, Award,
    MessageCircle, ChevronDown, ChevronUp, TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../components/ui/accordion';
import CategoryHeader from '../components/CategoryHeader';
import CategoryFooter from '../components/CategoryFooter';
import QuickInquiryModal from '../components/QuickInquiryModal';
import { useSiteData } from '../context/SiteDataContext';

const iconMap = {
    ShieldCheck, Users, Plane, Award
};

const CategoryPage = () => {
    const { categoryId } = useParams();
    const { siteData } = useSiteData();

    const [searchTerm, setSearchTerm] = useState('');
    const [subCategoryFilter, setSubCategoryFilter] = useState('All Categories');
    const [displayCount, setDisplayCount] = useState(12);
    const [expandedProducts, setExpandedProducts] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showStickyButton, setShowStickyButton] = useState(false);

    // If no category ID provided (e.g. legacy /oncology route), default to oncology
    const activeCategoryId = categoryId || 'oncology';
    const categoryData = siteData.categoryPages?.[activeCategoryId];

    // Handle scroll for sticky button
    useEffect(() => {
        const handleScroll = () => {
            setShowStickyButton(window.scrollY > 400);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Reset filters when category changes
    useEffect(() => {
        setSearchTerm('');
        setSubCategoryFilter('All Categories');
        setDisplayCount(12);
        setExpandedProducts({});
    }, [activeCategoryId]);

    if (!categoryData) {
        return <Navigate to="/" replace />;
    }

    const { hero, products, subCategories, trustBadges, faqs, testimonials, sectionContent } = categoryData;
    const whatsappNumber = siteData.general.whatsappNumber;

    // Filter products
    const filteredProducts = (products || []).filter(product => {
        const searchMatchString = `${product.name} ${product.indication} ${product.genericName}`.toLowerCase();
        const matchesSearch = searchMatchString.includes(searchTerm.toLowerCase());
        const matchesCategory = subCategoryFilter === 'All Categories' || product.category === subCategoryFilter;
        return matchesSearch && matchesCategory;
    });

    const displayedProducts = filteredProducts.slice(0, displayCount);

    const toggleProductExpansion = (productId) => {
        setExpandedProducts(prev => ({
            ...prev,
            [productId]: !prev[productId]
        }));
    };

    const handleWhatsAppClick = () => {
        window.open(`https://wa.me/${whatsappNumber || '919876543210'}`, '_blank');
    };

    const handleQuickInquiry = (product = null) => {
        setSelectedProduct(product);
        setIsModalOpen(true);
    };

    return (
        <div className="min-h-screen bg-white">
            <CategoryHeader title={hero?.title || "Medications"} whatsappNumber={whatsappNumber} />

            {/* Hero Section */}
            <section className="relative pt-32 pb-12 bg-[#cc2020] text-white">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center space-y-4">
                        <h1 className="text-4xl md:text-5xl font-bold">
                            {hero?.title || "Medications"}
                        </h1>
                        <p className="text-lg md:text-xl font-medium text-red-100">
                            {hero?.subtitle || "Affordable Treatments"}
                        </p>

                        {/* Badges strictly in the red header */}
                        <div className="flex flex-wrap justify-center gap-3 pt-4">
                            <span className="bg-white/10 text-white text-sm font-semibold px-4 py-2 rounded-full border border-white/20">
                                {hero?.stats?.[0]?.number || '30+'} Medications
                            </span>
                            <span className="bg-white/10 text-white text-sm font-semibold px-4 py-2 rounded-full border border-white/20">
                                Save up to 75%
                            </span>
                            <span className="bg-white/10 text-white text-sm font-semibold px-4 py-2 rounded-full border border-white/20">
                                WHO GMP Certified
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Products Section */}
            <section id="products" className="py-12 bg-gray-50">
                <div className="container mx-auto px-4">
                    {/* Search and Filter */}
                    <div className="max-w-4xl mx-auto mb-8">
                        <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 group focus-within:ring-2 focus-within:ring-red-600 focus-within:border-transparent transition-all">
                            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-red-600" />
                            <Input
                                type="text"
                                placeholder="Search medications by name, generic name, or indication..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-12 h-14 border-none shadow-none text-base focus-visible:ring-0 w-full rounded-lg"
                            />
                        </div>
                        <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between">
                            <p className="text-gray-600 font-medium text-sm">
                                Showing <strong>{Math.min(displayedProducts.length, filteredProducts.length)}</strong> medications
                            </p>
                        </div>
                    </div>

                    {/* Products Grid */}
                    {/* Products List */}
                    <div className="max-w-5xl mx-auto space-y-6 mb-12">
                        {displayedProducts.map((product) => (
                            <div
                                key={product.id || `${product.name}-${Math.random()}`}
                                className="bg-white rounded-[5px] shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07)] border border-gray-100 overflow-hidden"
                            >
                                <div className="p-5 lg:p-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
                                    {/* Column 1 - Product Info */}
                                    <div className="flex-1 md:max-w-[35%]">
                                        <h3 className="text-xl font-extrabold text-[#1a202c] mb-1">
                                            {product.name}
                                        </h3>
                                        <p className="text-[14px] text-[#4a5568] mb-2 font-medium">{product.genericName}</p>
                                        {product.indication && (
                                            <p className="text-[14px] font-bold text-[#c53030]">
                                                {product.indication}
                                            </p>
                                        )}
                                    </div>

                                    {/* Column 2 - US Price */}
                                    <div className="flex flex-col items-center md:items-start md:w-28 text-center md:text-left">
                                        <span className="text-[13px] text-gray-500 font-bold mb-1">US Price</span>
                                        <span className="text-[19px] text-[#8a94a6] font-extrabold line-through">{product.originalPrice || '$0'}</span>
                                    </div>

                                    {/* Column 3 - Our Price */}
                                    <div className="flex flex-col items-center md:items-start md:w-36 text-center md:text-left">
                                        <span className="text-[14px] text-gray-500 font-bold mb-1">Our Price</span>
                                        <span className="text-[28px] text-[#27ae60] font-extrabold leading-tight">{product.price}</span>
                                        {product.savings && (
                                            <span className="text-[14px] text-[#27ae60] font-extrabold mt-0.5">Save {product.savings}</span>
                                        )}
                                    </div>

                                    {/* Column 4 - Actions */}
                                    <div className="w-full md:w-44 space-y-2 flex-shrink-0">
                                        <button
                                            onClick={() => handleQuickInquiry(product)}
                                            className="w-full bg-[#1e9a4b] hover:bg-[#18803d] text-white rounded-[5px] py-1.5 px-4 font-extrabold flex items-center justify-center transition-all text-[15px] shadow-sm transform hover:scale-[1.02]"
                                        >
                                            <MessageCircle className="w-[18px] h-[18px] mr-2" /> Inquire
                                        </button>
                                        <button
                                            onClick={() => toggleProductExpansion(product.id)}
                                            className="w-full bg-white text-[#1a202c] hover:bg-gray-50 border-[1.5px] border-[#1a202c] rounded-[5px] py-1.5 px-4 font-extrabold flex items-center justify-center transition-all text-[14px] shadow-sm transform hover:scale-[1.02]"
                                        >
                                            {expandedProducts[product.id] ? (
                                                <><ChevronUp className="w-[18px] h-[18px] mr-2" /> Less Info</>
                                            ) : (
                                                <><ChevronDown className="w-[18px] h-[18px] mr-2" /> More Info</>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Expanded Area */}
                                {expandedProducts[product.id] && (
                                    <div className="p-5 lg:p-6 bg-white border-t border-gray-100">
                                        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
                                            {/* Column 1 */}
                                            <div className="space-y-4">
                                                <h4 className="font-bold text-[#1a202c] text-[15px]">Product Details</h4>
                                                <div className="text-[14px] text-[#4a5568] space-y-3">
                                                    <p className="leading-relaxed"><span className="text-[#1a202c] font-medium block mb-1">Description:</span> {product.description}</p>
                                                    <p><span className="text-[#1a202c] font-medium">Dosage:</span> {product.dosage || 'As prescribed by physician. Typically once or twice daily.'}</p>
                                                    <p><span className="text-[#1a202c] font-medium">Packaging:</span> {product.packaging}</p>
                                                </div>
                                            </div>

                                            {/* Column 2 */}
                                            <div className="space-y-4 mt-2 md:mt-0">
                                                <h4 className="font-bold text-[#1a202c] text-[15px]">Manufacturer & Storage</h4>
                                                <div className="text-[14px] text-[#4a5568] space-y-3">
                                                    <p><span className="text-[#1a202c] font-medium">Manufacturer:</span> {product.manufacturer}</p>
                                                    <p className="leading-relaxed"><span className="text-[#1a202c] font-medium">Storage:</span> {product.storage || 'Store below 25°C in a cool, dry place.'}</p>
                                                    <p className="leading-relaxed"><span className="text-[#1a202c] font-medium block mb-1">Side Effects:</span> {product.sideEffects || 'Common side effects may include nausea, headache, fatigue. Consult your doctor if symptoms persist.'}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-8 pt-8 flex justify-center w-full">
                                            <button
                                                onClick={handleWhatsAppClick}
                                                className="inline-flex bg-[#1e9a4b] hover:bg-[#18803d] text-white rounded-[5px] py-2 px-8 font-semibold items-center justify-center transition-colors shadow-sm text-[15px]"
                                            >
                                                <MessageCircle className="w-5 h-5 mr-2" /> Get Expert Consultation
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Load More */}
                    {displayCount < filteredProducts.length && (
                        <div className="text-center">
                            <Button
                                onClick={() => setDisplayCount(prev => prev + 12)}
                                variant="outline"
                                className="border-2 border-red-600 text-red-600 hover:bg-red-50 hover:text-red-700 px-8 py-6 rounded-xl font-bold text-lg shadow-sm"
                            >
                                Load More Medications
                            </Button>
                        </div>
                    )}

                    {/* No results */}
                    {filteredProducts.length === 0 && Array.isArray(products) && products.length > 0 && (
                        <div className="text-center py-16 bg-gray-50 rounded-2xl border border-gray-200 max-w-2xl mx-auto">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Search className="w-8 h-8 text-gray-400" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">No medications found</h3>
                            <p className="text-gray-600 mb-6">We couldn't find any medications matching "{searchTerm}".</p>
                            <Button
                                onClick={() => {
                                    setSearchTerm('');
                                    setSubCategoryFilter('All Categories');
                                }}
                                className="bg-red-600 hover:bg-red-700 text-white px-8"
                            >
                                Clear Search & Filters
                            </Button>
                        </div>
                    )}

                    {/* Empty Data State */}
                    {(!products || products.length === 0) && (
                        <div className="text-center py-16 bg-gray-50 rounded-2xl border border-gray-200 max-w-2xl mx-auto">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Products Coming Soon</h3>
                            <p className="text-gray-600 mb-6">We are currently updating our catalog for {hero?.title}. Please check back later or contact us directly for inquiries.</p>
                            <Button
                                onClick={handleWhatsAppClick}
                                className="bg-green-600 hover:bg-green-700 text-white px-8"
                            >
                                <MessageCircle className="w-4 h-4 mr-2" />
                                Contact via WhatsApp
                            </Button>
                        </div>
                    )}

                    {/* Contact Note */}
                    <div className="mt-16 max-w-4xl mx-auto bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-10 text-white text-center shadow-xl relative overflow-hidden">
                        {/* Decorative element */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-red-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 -mr-20 -mt-20"></div>

                        <div className="relative z-10">
                            <h3 className="text-3xl font-bold mb-4">{sectionContent?.midPageCTA?.title || "Can't Find Your Medication?"}</h3>
                            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                                {sectionContent?.midPageCTA?.description || "We have access to 5000+ generic medications. Our specialists can help you source any medication from verified Indian manufacturers at up to 80% savings."}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button
                                    onClick={handleWhatsAppClick}
                                    className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 rounded-xl font-bold text-lg flex items-center justify-center space-x-2 shadow-lg hover:shadow-green-900/50"
                                >
                                    <MessageCircle className="w-5 h-5" />
                                    <span>{sectionContent?.midPageCTA?.whatsappText || "Chat on WhatsApp"}</span>
                                </Button>
                                <Button
                                    onClick={() => handleQuickInquiry()}
                                    className="bg-white hover:bg-gray-100 text-red-600 px-8 py-6 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl"
                                >
                                    {sectionContent?.midPageCTA?.requestText || "Submit Custom Request"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            {testimonials && testimonials.length > 0 && (
                <section id="testimonials" className="py-20 bg-gray-50 border-t border-gray-200">
                    <div className="container mx-auto px-4">
                        <div className="text-center space-y-4 mb-16">
                            <h2 className="text-4xl font-bold text-gray-900">
                                {sectionContent?.testimonials?.title || "Patient Success Stories"}
                            </h2>
                            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                                {sectionContent?.testimonials?.description || `Hear from patients worldwide who saved thousands on their ${hero?.title?.toLowerCase() || 'medications'}`}
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                            {testimonials.map((testimonial, index) => (
                                <div
                                    key={index}
                                    className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 flex flex-col h-full hover:shadow-xl transition-shadow"
                                >
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <p className="font-bold text-gray-900 text-lg">{testimonial.name}</p>
                                            <p className="text-sm font-medium text-red-600">{testimonial.country}</p>
                                        </div>
                                        <div className="text-right bg-green-50 px-3 py-2 rounded-lg border border-green-100">
                                            <p className="text-xl font-bold text-green-600">{testimonial.savings}</p>
                                            <p className="text-[10px] text-green-800 uppercase tracking-wider font-bold">saved</p>
                                        </div>
                                    </div>
                                    <div className="space-y-3 mb-6 flex-grow">
                                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                                            <p className="text-sm text-gray-600">
                                                <span className="font-bold text-gray-900">Condition:</span> {testimonial.cancer || testimonial.condition || "Chronic"}
                                            </p>
                                            <p className="text-sm text-gray-600 mt-1">
                                                <span className="font-bold text-gray-900">Medication:</span> {testimonial.medication}
                                            </p>
                                        </div>
                                        <p className="text-gray-700 leading-relaxed italic relative px-4">
                                            <span className="absolute left-0 top-0 text-3xl text-red-300 opacity-50 font-serif">"</span>
                                            {testimonial.text || testimonial.testimonial}
                                            <span className="absolute right-0 bottom-0 text-3xl text-red-300 opacity-50 font-serif translate-y-4">"</span>
                                        </p>
                                    </div>
                                    <div className="flex items-center space-x-1 pt-4 border-t border-gray-100 mt-auto">
                                        {[...Array(testimonial.rating || 5)].map((_, i) => (
                                            <div key={i} className="w-5 h-5 text-yellow-400">
                                                <svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* FAQ Section */}
            {faqs && faqs.length > 0 && (
                <section id="faq" className="py-20 bg-white">
                    <div className="container mx-auto px-4">
                        <div className="max-w-3xl mx-auto">
                            <div className="text-center space-y-4 mb-12">
                                <h2 className="text-4xl font-bold text-gray-900">
                                    {sectionContent?.faqs?.title || "Frequently Asked Questions"}
                                </h2>
                                <p className="text-lg text-gray-600">
                                    {sectionContent?.faqs?.description || `Everything you need to know about ordering ${hero?.title?.toLowerCase() || 'medications'}`}
                                </p>
                            </div>

                            <Accordion type="single" collapsible className="space-y-4">
                                {faqs.map((faq, index) => (
                                    <AccordionItem
                                        key={index}
                                        value={`item-${index}`}
                                        className="bg-gray-50 rounded-xl border border-gray-200 px-6 overflow-hidden data-[state=open]:bg-white data-[state=open]:shadow-md transition-all duration-200"
                                    >
                                        <AccordionTrigger className="text-left font-bold text-gray-900 hover:text-red-600 py-5">
                                            <span className="flex items-center">
                                                <span className="text-red-600 mr-3 opacity-50">Q.</span>
                                                {faq.question}
                                            </span>
                                        </AccordionTrigger>
                                        <AccordionContent className="text-gray-600 leading-relaxed pb-6 text-base">
                                            <span className="flex items-start">
                                                <span className="text-gray-400 font-bold mr-3">A.</span>
                                                {faq.answer}
                                            </span>
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>

                            <div className="mt-12 text-center bg-red-50 p-8 rounded-2xl border border-red-100">
                                <p className="text-xl font-bold text-gray-900 mb-2">Still have questions?</p>
                                <p className="text-gray-600 mb-6">Our dedicated support team is available 24/7 to assist you.</p>
                                <Button
                                    onClick={handleWhatsAppClick}
                                    className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg rounded-xl font-bold flex items-center justify-center space-x-2 mx-auto shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                                >
                                    <MessageCircle className="w-5 h-5" />
                                    <span>Chat with Our Experts Now</span>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Final CTA */}
            <section className="py-24 bg-gradient-to-br from-red-700 to-gray-900 text-white relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>

                <div className="container mx-auto px-4 text-center relative z-10">
                    <div className="max-w-3xl mx-auto space-y-8">
                        <h2 className="text-4xl md:text-5xl font-bold leading-tight">
                            {sectionContent?.finalCTA?.title || "Ready to Save on Your Treatment?"}
                        </h2>
                        <p className="text-xl text-red-100 font-medium max-w-2xl mx-auto">
                            {sectionContent?.finalCTA?.description || `Get expert consultation and authentic ${hero?.title?.toLowerCase() || 'medications'} delivered directly to your doorstep.`}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                            <Button
                                onClick={handleWhatsAppClick}
                                className="bg-green-500 hover:bg-green-600 text-white px-8 py-6 text-lg rounded-xl font-bold flex items-center justify-center space-x-2 shadow-xl hover:shadow-2xl transition-all hover:scale-105"
                            >
                                <MessageCircle className="w-6 h-6" />
                                <span>{sectionContent?.finalCTA?.whatsappText || "Start WhatsApp Chat"}</span>
                            </Button>
                            <Button
                                onClick={() => handleQuickInquiry()}
                                className="bg-white hover:bg-gray-100 text-red-700 px-8 py-6 text-lg rounded-xl font-bold shadow-xl hover:shadow-2xl transition-all"
                            >
                                {sectionContent?.finalCTA?.requestText || "Request Free Consultation"}
                            </Button>
                        </div>
                        <div className="flex items-center justify-center space-x-6 pt-8 text-sm text-red-200 font-medium">
                            <span className="flex items-center">
                                <ShieldCheck className="w-4 h-4 mr-2" /> 45+ Years Exp
                            </span>
                            <span className="hidden md:flex items-center">
                                <Plane className="w-4 h-4 mr-2" /> Global Delivery
                            </span>
                            <span className="flex items-center">
                                <Award className="w-4 h-4 mr-2" /> 100% Authentic
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Sticky Contact Button */}
            {showStickyButton && (
                <button
                    onClick={handleWhatsAppClick}
                    className="fixed bottom-8 right-8 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 z-40 flex items-center space-x-3 group border-4 border-white"
                >
                    <MessageCircle className="w-7 h-7" />
                    <span className="hidden group-hover:inline-block pr-2 font-bold whitespace-nowrap">
                        Talk to Expert
                    </span>
                </button>
            )}

            <CategoryFooter categoryId={activeCategoryId} />

            <QuickInquiryModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                product={selectedProduct}
            />
        </div>
    );
};

export default CategoryPage;
